import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { formatDistanceToNow, format } from "date-fns";
import { ja } from "date-fns/locale";
import {
  Loader2,
  Star,
  StarOff,
  MoreVertical,
  Download,
  Share2,
  Trash2,
  Eye,
  Sparkles,
  CalendarIcon,
  FolderPlus,
  TagIcon,
  Folder as FolderIcon,
  Settings2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import {
  TagEditDialog,
  FolderAssignDialog,
  FolderManagerDialog,
  type Folder,
  type Tag,
} from "@/components/history/TagFolderDialogs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/history")({
  head: () => ({
    meta: [
      { title: "生成履歴 | KAGEM" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: HistoryPageWrapper,
});

type Row = {
  id: string;
  status: string;
  quality_tier: string | null;
  output_video_url: string | null;
  output_image_url: string | null;
  intermediate_image_url: string | null;
  kind: string;
  template_state: string | null;
  created_at: string;
  completed_at: string | null;
  is_favorite: boolean;
  has_watermark: boolean | null;
  is_blocked: boolean | null;
  title: string | null;
  category_id: string | null;
  category_params: Record<string, unknown> | null;
};

const STATUS_LABEL: Record<string, string> = {
  pending: "順番待ち",
  processing: "生成中",
  completed: "完了",
  failed: "失敗",
};

const STATUS_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  completed: "default",
  processing: "secondary",
  pending: "secondary",
  failed: "destructive",
};

const QUALITY_LABEL: Record<string, string> = {
  trial: "お試し",
  standard: "スタンダード",
  premium: "プレミアム",
  ultra: "ウルトラ",
};

const QUALITY_RANK: Record<string, number> = {
  trial: 0,
  standard: 1,
  premium: 2,
  ultra: 3,
};

const TEMPLATE_STATE_LABEL: Record<string, string> = {
  step1_processing: "Step1 合成中",
  step1_complete: "Step1 完了（承認待ち）",
  step1_failed: "Step1 失敗",
  step2_processing: "Step2 動画生成中",
  step2_failed: "Step2 失敗（返金済）",
  cancelled_after_step1: "Step1 で中止",
  completed: "完了",
};

type FilterKey = "all" | "completed" | "processing" | "failed" | "favorite" | "template";
type SortKey = "newest" | "oldest" | "quality";

const PAGE_SIZE = 20;

// Local QueryClient — root にも provider はあるが、このページは互換性のため独自clientを維持
import { queryClientDefaults } from "@/lib/queryClient";
import { formatCategorySummary } from "@/lib/transformationCategories";
let _client: QueryClient | null = null;
function getClient() {
  if (!_client) {
    _client = new QueryClient({
      defaultOptions: {
        ...queryClientDefaults,
        queries: { ...queryClientDefaults.queries, staleTime: 30_000, gcTime: 5 * 60_000 },
      },
    });
  }
  return _client;
}

function HistoryPageWrapper() {
  return (
    <QueryClientProvider client={getClient()}>
      <HistoryPage />
    </QueryClientProvider>
  );
}

function HistoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState<FilterKey>("all");
  const [sort, setSort] = useState<SortKey>("newest");
  const [qualityFilter, setQualityFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // フォルダ・タグ
  const [userId, setUserId] = useState<string | null>(null);
  const [folderFilter, setFolderFilter] = useState<string>("all"); // "all" | "none" | folder.id
  const [tagFilter, setTagFilter] = useState<Set<string>>(new Set());
  const [tagDialogId, setTagDialogId] = useState<string | null>(null);
  const [folderDialogId, setFolderDialogId] = useState<string | null>(null);
  const [folderManagerOpen, setFolderManagerOpen] = useState(false);

  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["generations", "history"],
    queryFn: async (): Promise<Row[]> => {
      const { data, error } = await supabase
        .from("generations")
        .select(
          "id, status, quality_tier, output_video_url, output_image_url, intermediate_image_url, kind, template_state, created_at, completed_at, is_favorite, has_watermark, is_blocked, title, category_id, category_params",
        )
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Row[];
    },
  });

  const { data: folders = [] } = useQuery({
    queryKey: ["folders", userId],
    enabled: !!userId,
    queryFn: async (): Promise<Folder[]> => {
      const { data, error } = await supabase
        .from("folders")
        .select("id, name, color")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Folder[];
    },
  });

  const { data: tags = [] } = useQuery({
    queryKey: ["tags", userId],
    enabled: !!userId,
    queryFn: async (): Promise<Tag[]> => {
      const { data, error } = await supabase
        .from("tags")
        .select("id, name")
        .order("name");
      if (error) throw error;
      return (data ?? []) as Tag[];
    },
  });

  const { data: folderItems = [] } = useQuery({
    queryKey: ["folder_items", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("folder_items")
        .select("folder_id, generation_id");
      if (error) throw error;
      return (data ?? []) as { folder_id: string; generation_id: string }[];
    },
  });

  const { data: genTags = [] } = useQuery({
    queryKey: ["generation_tags", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("generation_tags")
        .select("generation_id, tag_id");
      if (error) throw error;
      return (data ?? []) as { generation_id: string; tag_id: string }[];
    },
  });

  const foldersByGen = useMemo(() => {
    const m = new Map<string, string[]>();
    folderItems.forEach((fi) => {
      const arr = m.get(fi.generation_id) ?? [];
      arr.push(fi.folder_id);
      m.set(fi.generation_id, arr);
    });
    return m;
  }, [folderItems]);
  const tagsByGen = useMemo(() => {
    const m = new Map<string, string[]>();
    genTags.forEach((gt) => {
      const arr = m.get(gt.generation_id) ?? [];
      arr.push(gt.tag_id);
      m.set(gt.generation_id, arr);
    });
    return m;
  }, [genTags]);
  const folderById = useMemo(
    () => new Map(folders.map((f) => [f.id, f] as const)),
    [folders],
  );
  const tagById = useMemo(() => new Map(tags.map((t) => [t.id, t] as const)), [tags]);

  // フィルター・ソート適用
  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.slice();

    if (filter === "completed") rows = rows.filter((r) => r.status === "completed");
    else if (filter === "processing")
      rows = rows.filter((r) => r.status === "pending" || r.status === "processing");
    else if (filter === "failed") rows = rows.filter((r) => r.status === "failed");
    else if (filter === "favorite") rows = rows.filter((r) => r.is_favorite);
    else if (filter === "template") rows = rows.filter((r) => r.kind === "template_video");

    if (qualityFilter !== "all") {
      rows = rows.filter((r) => r.quality_tier === qualityFilter);
    }

    if (folderFilter === "none") {
      rows = rows.filter((r) => !foldersByGen.has(r.id));
    } else if (folderFilter !== "all") {
      rows = rows.filter((r) => foldersByGen.get(r.id)?.includes(folderFilter));
    }

    if (tagFilter.size > 0) {
      rows = rows.filter((r) => {
        const t = tagsByGen.get(r.id);
        if (!t) return false;
        return [...tagFilter].every((id) => t.includes(id));
      });
    }

    if (dateFrom) {
      const fromTs = dateFrom.getTime();
      rows = rows.filter((r) => new Date(r.created_at).getTime() >= fromTs);
    }
    if (dateTo) {
      const toTs = new Date(dateTo).setHours(23, 59, 59, 999);
      rows = rows.filter((r) => new Date(r.created_at).getTime() <= toTs);
    }

    if (sort === "newest") {
      rows.sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    } else if (sort === "oldest") {
      rows.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );
    } else if (sort === "quality") {
      rows.sort(
        (a, b) =>
          (QUALITY_RANK[b.quality_tier ?? ""] ?? -1) -
          (QUALITY_RANK[a.quality_tier ?? ""] ?? -1),
      );
    }

    return rows;
  }, [data, filter, sort, qualityFilter, dateFrom, dateTo, folderFilter, tagFilter, foldersByGen, tagsByGen]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  // ページ変更時の調整
  if (page !== currentPage) {
    setPage(currentPage);
  }

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["generations", "history"] });
  };
  const invalidateFolders = () => {
    queryClient.invalidateQueries({ queryKey: ["folders", userId] });
    queryClient.invalidateQueries({ queryKey: ["folder_items", userId] });
  };
  const invalidateTags = () => {
    queryClient.invalidateQueries({ queryKey: ["tags", userId] });
    queryClient.invalidateQueries({ queryKey: ["generation_tags", userId] });
  };

  const toggleFavorite = async (row: Row) => {
    const next = !row.is_favorite;
    const { error } = await supabase
      .from("generations")
      .update({ is_favorite: next })
      .eq("id", row.id);
    if (error) {
      toast.error("更新に失敗しました");
      return;
    }
    toast.success(next ? "お気に入りに追加しました" : "お気に入りから外しました");
    invalidate();
  };

  const deleteGeneration = async (id: string) => {
    // 楽観的更新：即座にリストから消す
    const prev = queryClient.getQueryData<Row[]>(["generations", "history"]);
    if (prev) {
      queryClient.setQueryData<Row[]>(
        ["generations", "history"],
        prev.filter((r) => r.id !== id),
      );
    }
    const { error } = await supabase
      .from("generations")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      // ロールバック
      if (prev) queryClient.setQueryData(["generations", "history"], prev);
      toast.error("削除に失敗しました");
      return;
    }
    toast.success("削除しました");
    void queryClient.refetchQueries({ queryKey: ["generations", "history"] });
  };


  const handleShare = async (row: Row) => {
    const path = row.kind === "image" ? `/create/image/result/${row.id}` : `/result/${row.id}`;
    const url = `${window.location.origin}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URLをコピーしました");
    } catch {
      toast.error("コピーに失敗しました");
    }
  };

  const handleDownload = async (row: Row) => {
    const isImage = row.kind === "image";
    const url = isImage ? row.output_image_url : row.output_video_url;
    if (!url) {
      toast.error(isImage ? "画像URLが見つかりません" : "動画URLが見つかりません");
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    a.download = isImage ? `image-${row.id}.png` : `video-${row.id}.mp4`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <main className="min-h-screen bg-background px-4 py-10">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">履歴</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              これまでに生成した動画の一覧です。
            </p>
          </div>
          <Button asChild>
            <Link to="/create">
              <Sparkles className="mr-2 h-4 w-4" />
              新規作成
            </Link>
          </Button>
        </div>

        {/* フィルター・ソートバー */}
        <div className="mt-6 space-y-3 rounded-xl border bg-card p-4">
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["all", "すべて"],
                ["template", "テンプレ動画"],
                ["completed", "完了済み"],
                ["processing", "生成中"],
                ["failed", "失敗"],
                ["favorite", "お気に入り"],
              ] as const
            ).map(([key, label]) => (
              <Button
                key={key}
                size="sm"
                variant={filter === key ? "default" : "outline"}
                onClick={() => {
                  setFilter(key);
                  setPage(1);
                }}
              >
                {label}
              </Button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={sort}
              onValueChange={(v) => {
                setSort(v as SortKey);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">新しい順</SelectItem>
                <SelectItem value="oldest">古い順</SelectItem>
                <SelectItem value="quality">品質順</SelectItem>
              </SelectContent>
            </Select>

            <Select
              value={qualityFilter}
              onValueChange={(v) => {
                setQualityFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="品質グレード" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">すべての品質</SelectItem>
                <SelectItem value="trial">お試し</SelectItem>
                <SelectItem value="standard">スタンダード</SelectItem>
                <SelectItem value="premium">プレミアム</SelectItem>
                <SelectItem value="ultra">ウルトラ</SelectItem>
              </SelectContent>
            </Select>

            <DateRangeButton
              label="開始日"
              date={dateFrom}
              onChange={(d) => {
                setDateFrom(d);
                setPage(1);
              }}
            />
            <DateRangeButton
              label="終了日"
              date={dateTo}
              onChange={(d) => {
                setDateTo(d);
                setPage(1);
              }}
            />

            {(dateFrom || dateTo || qualityFilter !== "all") && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setDateFrom(undefined);
                  setDateTo(undefined);
                  setQualityFilter("all");
                  setPage(1);
                }}
              >
                条件をクリア
              </Button>
            )}
          </div>

          {/* フォルダ */}
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <span className="text-xs font-semibold text-muted-foreground">フォルダ:</span>
            <Button
              size="sm"
              variant={folderFilter === "all" ? "default" : "outline"}
              onClick={() => {
                setFolderFilter("all");
                setPage(1);
              }}
            >
              すべて
            </Button>
            <Button
              size="sm"
              variant={folderFilter === "none" ? "default" : "outline"}
              onClick={() => {
                setFolderFilter("none");
                setPage(1);
              }}
            >
              未分類
            </Button>
            {folders.map((f) => (
              <Button
                key={f.id}
                size="sm"
                variant={folderFilter === f.id ? "default" : "outline"}
                onClick={() => {
                  setFolderFilter(f.id);
                  setPage(1);
                }}
                className="gap-1.5"
              >
                <span
                  className="inline-block h-2.5 w-2.5 rounded-sm"
                  style={{ background: f.color ?? "#94a3b8" }}
                />
                {f.name}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFolderManagerOpen(true)}
              className="ml-auto"
            >
              <Settings2 className="mr-1 h-4 w-4" />
              フォルダ管理
            </Button>
          </div>

          {/* タグ */}
          {tags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <span className="text-xs font-semibold text-muted-foreground">タグ:</span>
              {tags.map((t) => {
                const active = tagFilter.has(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      setTagFilter((prev) => {
                        const n = new Set(prev);
                        if (n.has(t.id)) n.delete(t.id);
                        else n.add(t.id);
                        return n;
                      });
                      setPage(1);
                    }}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-xs transition",
                      active
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary",
                    )}
                  >
                    #{t.name}
                  </button>
                );
              })}
              {tagFilter.size > 0 && (
                <Button size="sm" variant="ghost" onClick={() => setTagFilter(new Set())}>
                  解除
                </Button>
              )}
            </div>
          )}
        </div>

        {/* コンテンツ */}
        {error ? (
          <p className="mt-8 text-sm text-destructive">
            {(error as Error).message}
          </p>
        ) : isLoading ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 flex flex-col items-center gap-4 text-center">
            <p className="text-lg font-medium">まだ動画がありません</p>
            <p className="text-sm text-muted-foreground">
              最初の動画を作ってみましょう！
            </p>
            <Button asChild>
              <Link to="/create">
                <Sparkles className="mr-2 h-4 w-4" />
                新規作成
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <ul className="mt-6 grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-4 sm:grid-cols-[repeat(auto-fill,minmax(200px,1fr))]">
              {pageRows.map((r) => (
                <HistoryCard
                  key={r.id}
                  row={r}
                  onOpen={() => {
                    // 履歴からは常に結果ビューア（/result/:id）を別タブで開く
                    const path =
                      r.kind === "image"
                        ? `/create/image/result/${r.id}`
                        : `/result/${r.id}`;
                    window.open(path, "_blank", "noopener,noreferrer");
                  }}

                  onToggleFavorite={() => toggleFavorite(r)}
                  onShare={() => handleShare(r)}
                  onDownload={() => handleDownload(r)}
                  onDelete={() => setDeleteId(r.id)}
                  onEditTags={() => setTagDialogId(r.id)}
                  onAssignFolder={() => setFolderDialogId(r.id)}
                  tags={(tagsByGen.get(r.id) ?? []).map((id) => tagById.get(id)).filter(Boolean) as Tag[]}
                  folders={(foldersByGen.get(r.id) ?? []).map((id) => folderById.get(id)).filter(Boolean) as Folder[]}
                />
              ))}
            </ul>

            {totalPages > 1 && (
              <Pagination className="mt-8">
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.max(1, p - 1));
                      }}
                      className={cn(
                        currentPage === 1 && "pointer-events-none opacity-50",
                      )}
                    />
                  </PaginationItem>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (n) => (
                      <PaginationItem key={n}>
                        <PaginationLink
                          href="#"
                          isActive={n === currentPage}
                          onClick={(e) => {
                            e.preventDefault();
                            setPage(n);
                          }}
                        >
                          {n}
                        </PaginationLink>
                      </PaginationItem>
                    ),
                  )}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setPage((p) => Math.min(totalPages, p + 1));
                      }}
                      className={cn(
                        currentPage === totalPages &&
                          "pointer-events-none opacity-50",
                      )}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={deleteId !== null}
        onOpenChange={(open) => !open && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>この動画を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              この操作は取り消せません。動画は履歴から非表示になります。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) deleteGeneration(deleteId);
                setDeleteId(null);
              }}
            >
              削除する
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {userId && tagDialogId && (
        <TagEditDialog
          open={true}
          onOpenChange={(o) => !o && setTagDialogId(null)}
          generationId={tagDialogId}
          userId={userId}
          allTags={tags}
          initialTagIds={tagsByGen.get(tagDialogId) ?? []}
          onSaved={invalidateTags}
        />
      )}
      {userId && folderDialogId && (
        <FolderAssignDialog
          open={true}
          onOpenChange={(o) => !o && setFolderDialogId(null)}
          generationId={folderDialogId}
          userId={userId}
          folders={folders}
          initialFolderIds={foldersByGen.get(folderDialogId) ?? []}
          onSaved={invalidateFolders}
          onManageFolders={() => {
            setFolderDialogId(null);
            setFolderManagerOpen(true);
          }}
        />
      )}
      {userId && (
        <FolderManagerDialog
          open={folderManagerOpen}
          onOpenChange={setFolderManagerOpen}
          userId={userId}
          folders={folders}
          onChanged={invalidateFolders}
        />
      )}
    </main>
  );
}

function DateRangeButton({
  label,
  date,
  onChange,
}: {
  label: string;
  date: Date | undefined;
  onChange: (d: Date | undefined) => void;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(!date && "text-muted-foreground")}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, "yyyy/MM/dd") : label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={onChange}
          initialFocus
          className={cn("p-3 pointer-events-auto")}
        />
      </PopoverContent>
    </Popover>
  );
}

function HistoryCard({
  row,
  onOpen,
  onToggleFavorite,
  onShare,
  onDownload,
  onDelete,
  onEditTags,
  onAssignFolder,
  tags,
  folders,
}: {
  row: Row;
  onOpen: () => void;
  onToggleFavorite: () => void;
  onShare: () => void;
  onDownload: () => void;
  onDelete: () => void;
  onEditTags: () => void;
  onAssignFolder: () => void;
  tags: Tag[];
  folders: Folder[];
}) {
  const created = new Date(row.created_at);
  const relative = formatDistanceToNow(created, { addSuffix: true, locale: ja });
  const absolute = format(created, "M月d日 HH:mm");

  const isImageKind = row.kind === "image";
  const isFailed =
    row.status === "failed" ||
    row.template_state === "step1_failed" ||
    row.template_state === "step2_failed";
  return (
    <li className="group overflow-hidden rounded-xl border bg-card text-card-foreground transition-shadow hover:shadow-md">
      <button
        type="button"
        onClick={onOpen}
        className={cn(
          "relative block w-full overflow-hidden bg-muted",
          "aspect-[9/16]",
        )}
        aria-label={isImageKind ? "画像を開く" : "動画を開く"}
      >
        {row.is_blocked ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 bg-destructive/10 p-4 text-center">
            <span className="text-xs font-semibold text-destructive">⚠ 審査中</span>
            <span className="text-[11px] text-muted-foreground">
              通報を受け確認中です
            </span>
          </div>
        ) : isFailed ? (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-4 text-center">
            <span className="text-2xl">⚠️</span>
            <span className="text-xs text-muted-foreground">生成失敗</span>
          </div>
        ) : isImageKind && row.output_image_url ? (
          <img
            src={row.output_image_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-contain transition-transform group-hover:scale-105"
            onError={async (e) => {
              const img = e.currentTarget;
              if (img.dataset.retried === "1") return;
              img.dataset.retried = "1";
              const m = row.output_image_url?.match(/\/object\/sign\/([^/]+)\/([^?]+)/);
              if (!m) return;
              const [, bucket, path] = m;
              const { data } = await supabase.storage
                .from(bucket)
                .createSignedUrl(decodeURIComponent(path), 60 * 60);
              if (data?.signedUrl) img.src = data.signedUrl;
            }}
          />
        ) : row.output_video_url ? (
          <video
            src={row.output_video_url}
            muted
            playsInline
            preload="metadata"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            {...({ loading: "lazy" } as any)}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
          />
        ) : row.kind === "template_video" && row.intermediate_image_url ? (
          <img
            src={row.intermediate_image_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover opacity-80 transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-1 p-4 text-center">
            <span className="text-2xl">⏳</span>
            <span className="text-xs text-muted-foreground">処理中...</span>
          </div>
        )}
        {row.kind === "template_video" && (
          <span className="absolute left-2 right-2 top-2 block max-w-[calc(100%-1rem)] truncate rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-bold text-primary-foreground shadow">
            {row.category_id
              ? `📦 ${formatCategorySummary(row.category_id, row.category_params) ?? "変身テンプレ"}`
              : "変身テンプレ"}
          </span>
        )}
      </button>

      <div className="space-y-1.5 p-3">
        <div className="flex items-center justify-between gap-2">
          <Badge
            variant={STATUS_VARIANT[row.status] ?? "outline"}
            className="max-w-[70%] truncate whitespace-nowrap"
          >
            {row.kind === "template_video" && row.template_state && row.status !== "completed" && row.status !== "failed"
              ? (TEMPLATE_STATE_LABEL[row.template_state] ?? row.template_state)
              : (STATUS_LABEL[row.status] ?? row.status)}
          </Badge>
          <span className="shrink-0 text-xs text-muted-foreground">
            {row.kind === "template_video"
              ? "テンプレ"
              : (QUALITY_LABEL[row.quality_tier ?? ""] ?? row.quality_tier ?? "-")}
          </span>
        </div>

        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold" title={row.title ?? undefined}>
              {row.title?.trim() || (row.kind === "image" ? "無題の画像" : "無題の動画")}
            </p>
            <p className="text-xs text-muted-foreground" title={absolute}>{relative}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={onToggleFavorite}
              aria-label={
                row.is_favorite ? "お気に入りから外す" : "お気に入りに追加"
              }
            >
              {row.is_favorite ? (
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ) : (
                <StarOff className="h-4 w-4" />
              )}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-8 w-8"
                  aria-label="メニュー"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onOpen}>
                  <Eye className="mr-2 h-4 w-4" /> プレビュー
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onDownload}
                  disabled={!row.output_video_url}
                >
                  <Download className="mr-2 h-4 w-4" /> ダウンロード
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onShare}>
                  <Share2 className="mr-2 h-4 w-4" /> 共有
                </DropdownMenuItem>
                {row.kind !== "template_video" && row.category_id == null && (
                  <DropdownMenuItem
                    onClick={() => {
                      if (row.kind === "image") {
                        window.location.href = `/create/image?from=${row.id}`;
                      } else {
                        window.location.href = `/create/video?from=${row.id}`;
                      }
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" /> 同じ設定で再生成
                  </DropdownMenuItem>
                )}
                {row.kind === "template_video" &&
                  (row.category_id === "freeform" ||
                    row.category_id === "freeform_text" ||
                    row.category_id === "freeform_image" ||
                    row.category_id === "freeform_outfit" ||
                    row.category_id === "freeform_age") && (
                    <DropdownMenuItem
                      onClick={() => {
                        window.location.href = `/create/template?from=${row.id}`;
                      }}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" /> 同じ設定で再生成
                    </DropdownMenuItem>
                  )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onEditTags}>
                  <TagIcon className="mr-2 h-4 w-4" /> タグを編集
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onAssignFolder}>
                  <FolderPlus className="mr-2 h-4 w-4" /> フォルダに追加
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={onDelete}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> 削除
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {(folders.length > 0 || tags.length > 0) && (
          <div className="flex flex-wrap gap-1 pt-1">
            {folders.map((f) => (
              <span
                key={f.id}
                className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]"
                style={f.color ? { borderColor: f.color, color: f.color } : undefined}
              >
                <FolderIcon className="h-3 w-3" />
                {f.name}
              </span>
            ))}
            {tags.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
              >
                #{t.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}
